import { getCurrentUser } from "@/auth/firebase";
import { API_V2_ROOT } from "@/config";
import { envLocker } from "@/reducers/environments";
import { masqueradeToken } from "@/util";

import type {
  ApiToken,
  CreateApiTokenInput,
  CreateApiTokenResponse,
  CreateServiceAccountInput,
  PermissionCatalog,
  ServiceAccount,
  UpdateServiceAccountInput,
} from "./types";

const BASE = `${API_V2_ROOT}/v2/internal/service_accounts`;

async function authHeaders(): Promise<Record<string, string>> {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error("Not authenticated");
  }
  const token = await currentUser.getIdToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    // The apiv2 InternalAuth middleware requires an environment to resolve the
    // caller's account and role; service-account endpoints are account-scoped
    // but still authenticate through the currently selected environment.
    "X-Env-ID": envLocker.getEnvID() ?? "",
  };
  const masq = masqueradeToken();
  if (masq) {
    headers["X-Masquerade-Token"] = masq;
  }
  return headers;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers ?? {}) },
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body?.error || body?.status || message;
    } catch {
      // Non-JSON error body; fall back to the generic message.
    }
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export async function listServiceAccounts(): Promise<ServiceAccount[]> {
  const res = await request<{ data: ServiceAccount[] }>("/");
  return res.data;
}

export function createServiceAccount(
  input: CreateServiceAccountInput,
): Promise<ServiceAccount> {
  return request<ServiceAccount>("/", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateServiceAccount(
  id: string,
  input: UpdateServiceAccountInput,
): Promise<void> {
  return request<void>(`/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteServiceAccount(id: string): Promise<void> {
  return request<void>(`/${id}`, { method: "DELETE" });
}

export async function listApiTokens(
  serviceAccountId: string,
): Promise<ApiToken[]> {
  const res = await request<{ data: ApiToken[] }>(`/${serviceAccountId}/keys`);
  return res.data;
}

export function createApiToken(
  serviceAccountId: string,
  input: CreateApiTokenInput,
): Promise<CreateApiTokenResponse> {
  return request<CreateApiTokenResponse>(`/${serviceAccountId}/keys`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function revokeApiToken(
  serviceAccountId: string,
  apiTokenId: string,
): Promise<void> {
  return request<void>(`/${serviceAccountId}/keys/${apiTokenId}/revoke`, {
    method: "POST",
  });
}

export async function listPermissionCatalog(): Promise<PermissionCatalog> {
  const res = await request<{ permissions: PermissionCatalog }>("/permissions");
  return res.permissions;
}
